import { Modal, Select, Table, Input } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import ChantButton from "../../../../CommonComponents/ChantButton/ChantButton";
import dayjs from "dayjs";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../store/adminData/adminData";
import { updateOrderNote } from "../../../../services/firebaseService/endPoints/admin/store";
import logger from "../../../../utils/logger";

const EditOrderModal = ({
  editableOrderId,
  tableData,
  editedRecords,
  editStatus,
  setEditStatus,
  handleRecordStatusChange,
  handleUpdateStatus,
  statusUpdateLoading,
}) => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const options = [
    { label: "Shipped", value: "shipped" },
    { label: "Picked up", value: "pickedup" },
    { label: "Refunded", value: "refunded" },
    { label: "Unclaimed", value: "unclaimed" },
    { label: "Unfulfilled", value: "unfulfilled" },
  ];

  const currentOrderItems = useMemo(() => {
    if (!editableOrderId) return [];
    return (tableData || []).filter((rec) => rec.id === editableOrderId);
  }, [editableOrderId, tableData]);

  const [disableSave, setDisableSave] = useState(false);
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    const firstItem = currentOrderItems[0];
    if (editStatus && firstItem) {
      setNoteText(firstItem?.orderNote?.text ?? "");
    }
  }, [editStatus, currentOrderItems]);

  const handleSave = async () => {
    const firstItem = currentOrderItems[0];
    if (groupData?.id && firstItem?.id != null) {
      try {
        await updateOrderNote(groupData.id, firstItem.id, {
          text: noteText?.trim() ?? "",
          updatedOn: Date.now(),
        });
      } catch (err) {
        logger.error("Error saving order note:", err);
      }
    }
    handleUpdateStatus();
  };

  const editableTableCols = [
    {
      title: "Status",
      dataIndex: "status",
      className: "header-white",
      render: (data, record) => {
        let records = editedRecords || {};
        records[record.id] = { ...(editedRecords[record.id] || {}) };

        let value = records[record.id][record.dbIndex] || data;

        return (
          <Select
            disabled={statusUpdateLoading}
            value={value}
            popupMatchSelectWidth={false}
            onChange={(value) => {
              if (value === "select") {
                setDisableSave(true);
                return;
              } else {
                setDisableSave(false);
              }

              let records = editedRecords || {};
              records[record.id] = {
                ...(editedRecords[record.id] || {}),
              };
              records[record.id][record.dbIndex] = value;

              handleRecordStatusChange(records);
            }}
            style={{ width: "100%" }}
            options={options?.map((item) => ({
              value: item.value,
              label: <span>{item.label}</span>,
            }))}
          />
        );
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      className: "header-white",
    },
    {
      title: "Status Updated",
      dataIndex: "statusUpdateTimestamp",
      className: "header-white",
      render: (data) => {
        let date = dayjs.unix(data);
        if (date.isValid() && data) {
          return <span>{dayjs.unix(data).format("MMM D") || ""}</span>;
        }
        return "";
      },
    },
    {
      title: "Name",
      dataIndex: "customerName",
      className: "header-white",
      render: (data) => <span>{data}</span>,
    },
    {
      title: "Address",
      dataIndex: "address",
      className: "header-white",
      render: (data) => {
        return <span>{data}</span>;
      },
    },
    {
      title: "Item",
      dataIndex: "itemName",
      className: "header-white",
      render: (data) => {
        return <span>{data}</span>;
      },
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      className: "header-white",
      render: (data) => {
        return <span>{data}</span>;
      },
    },
    {
      title: "Size",
      dataIndex: "size",
      className: "header-white",
      render: (data) => {
        return <span>{data}</span>;
      },
    },
    {
      title: "Color",
      dataIndex: "color",
      className: "header-white",
      render: (data) => {
        return <span>{data}</span>;
      },
    },
  ];

  return (
    <Modal
      open={editStatus}
      onCancel={() => {
        setEditStatus(false);
      }}
      width={950}
      title={currentOrderItems[0] ? `Order #${currentOrderItems[0].id}` : ""}
      centered
      footer={[
        <ChantButton
          key="submit"
          type="primary"
          onClick={handleSave}
          disabled={disableSave || statusUpdateLoading}
          loading={statusUpdateLoading}
        >
          Save
        </ChantButton>,
      ]}
    >
      <div
        style={{
          paddingTop: 12,
          width: "100%",
          maxHeight: "70vh",
          overflow: "auto",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              color: "#666",
            }}
          >
            Status
          </div>
          <Table
            bordered
            columns={editableTableCols}
            dataSource={currentOrderItems}
            rowKey={(record) => `${record.id}|${record.dbIndex}`}
            pagination={false}
            className="table-backdrop"
            bodyStyle={{ backgroundColor: "#ffffff" }}
            scroll={{ x: "max-content" }}
          />
        </div>

        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            paddingTop: 16,
            marginTop: 16,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              color: "#666",
            }}
          >
            Order note
          </div>
          <Input.TextArea
            placeholder="Enter order note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={3}
            style={{ width: "100%", resize: "vertical" }}
          />
          {currentOrderItems[0]?.orderNote?.updatedOn && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: "#999",
              }}
            >
              Last updated{" "}
              {dayjs(currentOrderItems[0].orderNote.updatedOn).format(
                "MMM DD, YYYY h:mm A",
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditOrderModal;
